// Import guests from CSV file exported from Google Sheets
// 
// CSV Format Expected (first row is headers):
// FirstName,LastName,InvitedAs,Email,Phone
// John,Smith,allDay,,
// Jane,Doe,evening,,
//
// InvitedAs should be: "allDay" or "evening"

import { doc, setDoc, getDoc } from "firebase/firestore";
import { db } from '../firebase';

// Generate guest code from first and last name
const generateGuestCode = (firstName, lastName) => {
  return `${firstName.toLowerCase().trim()}${lastName.toLowerCase().trim()}`;
};

// Parse CSV text to array of guest objects
export const parseCSVToGuests = (csvText) => {
  const lines = csvText.trim().split('\n');
  // Skip header row (line 0) and process data rows
  
  const guests = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim());
    
    if (values.length < 3) continue; // Skip invalid rows
    
    const firstName = values[0];
    const lastName = values[1];
    const invitedAs = values[2] === 'allDay' ? 'allDay' : 'evening';
    const email = values[3] || '';
    const phone = values[4] || '';
    
    if (!firstName || !lastName) continue; // Skip if no name
    
    guests.push({
      guestCode: generateGuestCode(firstName, lastName),
      firstName: firstName,
      lastName: lastName,
      email: email,
      phone: phone,
      invitedAs: invitedAs,
      hasRSVPd: false,
      rsvpStatus: "",
      rsvpType: "",
      foodChoice: "",
      songRequest: "",
      updatedAt: null,
      createdAt: new Date()
    });
  }
  
  return guests;
};

// Import guests from parsed CSV data
export const importGuestsToFirestore = async (guests) => {
  try {
    // Check if user is authenticated
    const { auth } = await import('../firebase');
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be signed in to import guests. Please sign out and sign back in.');
    }
    
    console.log('Importing as user:', currentUser.email);
    
    // Force token refresh to ensure Firestore has latest auth state
    try {
      await currentUser.getIdToken(true);
      console.log('Auth token refreshed successfully');
      // Small delay to ensure token propagates to Firestore
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (tokenError) {
      console.error('Error refreshing token:', tokenError);
    }
    
    let added = 0;
    let updated = 0;
    let skipped = 0;
    
    for (const guest of guests) {
      try {
        const guestRef = doc(db, "guests", guest.guestCode);
        const guestSnap = await getDoc(guestRef);
        
        if (!guestSnap.exists()) {
          // New guest - add them
          await setDoc(guestRef, guest);
          console.log(`Added guest: ${guest.firstName} ${guest.lastName} (${guest.guestCode})`);
          added++;
        } else {
          const existingGuest = guestSnap.data();
          
          // Guest exists - only update basic info if they haven't RSVP'd
          if (!existingGuest.hasRSVPd) {
            await setDoc(guestRef, {
              ...existingGuest,
              firstName: guest.firstName,
              lastName: guest.lastName,
              invitedAs: guest.invitedAs,
              // Keep existing email/phone if CSV is empty
              email: guest.email || existingGuest.email,
              phone: guest.phone || existingGuest.phone
            });
            console.log(`Updated guest: ${guest.firstName} ${guest.lastName} (${guest.guestCode})`);
            updated++;
          } else {
            console.log(`Skipped guest (already RSVP'd): ${guest.firstName} ${guest.lastName}`);
            skipped++;
          }
        }
      } catch (guestError) {
        console.error(`Error processing ${guest.firstName} ${guest.lastName}:`, guestError);
        throw guestError; // Re-throw to trigger main catch block
      }
    }
    
    return { success: true, added, updated, skipped, total: guests.length };
  } catch (error) {
    console.error("Error importing guests:", error);
    
    // Provide more specific error messages
    if (error.code === 'permission-denied') {
      return { 
        success: false, 
        error: 'Permission denied. Please sign out and sign back in, then try again. Your email must be in the admin list.' 
      };
    } else if (error.message.includes('auth')) {
      return { 
        success: false, 
        error: 'Authentication error. Please refresh the page and sign in again.' 
      };
    }
    
    return { success: false, error: error.message };
  }
};

export { generateGuestCode };
